import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { youtubeNotificationService, YouTubeNotificationDisplay } from '../services/youtubeNotificationService';
import { useAuth } from '../hooks/useAuth';
import { Sparkles, Youtube, Lock, ChevronRight, PlayCircle } from 'lucide-react';

const YouTubeNotificationBanner: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState<YouTubeNotificationDisplay[]>([]);
  const [isPaused, setIsPaused] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch notifications on mount and periodically
  useEffect(() => {
    fetchNotifications();

    // Refresh every 2 minutes to get new notifications
    refreshIntervalRef.current = setInterval(() => {
      fetchNotifications();
    }, 2 * 60 * 1000);

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, []);

  const fetchNotifications = async () => {
    try {
      const data = await youtubeNotificationService.getActiveNotifications();
      if (data.length > 0) {
        setNotifications(data);
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    } catch (error) {
      console.error('Error fetching YouTube notifications:', error);
      setIsVisible(false);
    }
  };

  // Don't render if no notifications
  if (!isVisible || notifications.length === 0) {
    return null;
  }

  // Create duplicate items for seamless loop
  const duplicatedNotifications = [...notifications, ...notifications, ...notifications, ...notifications];

  const handleLockedClick = () => {
    navigate('/login');
  };

  const handleLockedKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleLockedClick();
    }
  };

  // Format date to Vietnamese format (dd/mm/yyyy)
  const formatDate = (dateString?: string): string => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return '';
    }
  };

  // Get category text with date
  const getCategoryText = (notification: YouTubeNotificationDisplay): string => {
    const dateStr = formatDate(notification.ngay_dang);
    if (dateStr) {
      return t('common.keyVipYoutubeDate', { date: dateStr });
    }
    return t('common.keyVipYoutube');
  };

  return (
    <div className="w-full bg-gradient-to-r from-indigo-50/90 via-purple-50/90 to-pink-50/90 backdrop-blur-md border-y border-indigo-100/50 shadow-sm relative overflow-hidden h-12 flex items-center">
      <style>{`
        @keyframes banner-scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-banner-scroll {
          animation: banner-scroll 220s linear infinite;
        }
        .animate-banner-scroll:hover {
          animation-play-state: paused;
        }
      `}</style>

      {/* Label Badge */}
      <div className="hidden sm:flex absolute left-0 z-20 h-full bg-gradient-to-r from-white via-white to-transparent px-4 items-center">
        <div className="flex items-center gap-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-sm shadow-indigo-200 whitespace-nowrap">
          <Sparkles className="w-3 h-3" />
          <span>{t('common.latest')}</span>
        </div>
      </div>

      {/* Main Scroll Content */}
      <div
        className="flex items-center gap-8 whitespace-nowrap animate-banner-scroll pl-4 sm:pl-32"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {duplicatedNotifications.map((notification, index) => (
          <div
            key={`${notification.youtube_link}-${index}`}
            className="flex items-center gap-3 select-none group"
            onClick={!isAuthenticated ? handleLockedClick : undefined}
            onKeyDown={!isAuthenticated ? handleLockedKeyDown : undefined}
            role={!isAuthenticated ? 'button' : undefined}
            tabIndex={!isAuthenticated ? 0 : undefined}
          >
            {/* Date Badge */}
            <span className="text-xs font-semibold px-2 py-0.5 rounded-md bg-white border border-indigo-100 text-indigo-600 shadow-sm">
              {getCategoryText(notification)}
            </span>

            {isAuthenticated ? (
              <a
                href={notification.youtube_link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-slate-600 hover:text-indigo-600 transition-colors group/link"
              >
                <Youtube className="w-4 h-4 text-red-500 group-hover/link:scale-110 transition-transform" />
                <span className="text-sm font-medium group-hover/link:underline decoration-indigo-200 underline-offset-4">
                  {notification.title}
                </span>

                {(notification.quan_doan || notification.mon_phai || notification.viral_key) && (
                  <>
                    <ChevronRight className="w-3 h-3 text-slate-300" />
                    <span className="flex items-center gap-1.5">
                      {[
                        notification.quan_doan,
                        notification.mon_phai,
                        notification.viral_key,
                      ]
                        .filter(Boolean)
                        .map((tag, i) => (
                          <span key={i} className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 uppercase tracking-wide border border-slate-200">
                            {tag}
                          </span>
                        ))}
                    </span>
                  </>
                )}
              </a>
            ) : (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  handleLockedClick();
                }}
                className="flex items-center gap-2 cursor-pointer group/lock"
                title={t('common.loginToView')}
              >
                <Lock className="w-3.5 h-3.5 text-slate-400 group-hover/lock:text-indigo-500 transition-colors" />
                <span className="text-sm font-medium text-slate-400 group-hover/lock:text-indigo-500 transition-colors">
                  {t('common.loginToView')}
                </span>
              </button>
            )}

            <div className="w-1 h-1 rounded-full bg-slate-300 mx-2" />
          </div>
        ))}
      </div>

      {/* Fade Overlay Right */}
      <div className="absolute right-0 z-20 h-full w-24 bg-gradient-to-l from-white to-transparent pointer-events-none" />
    </div>
  );
};

export default YouTubeNotificationBanner;

